<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'

import { useUserStore } from '@provise/stores/user'
import { useApi } from '@provise/composables/api'
import Navbar from '@provise/components/navbar/navbar.vue'

import { useLockStore } from '@provise/stores/lock'
import { useUiStore } from '@provise/stores/ui'
import { useInactivityTracker } from '@provise/composables/itracker.js'
import MessagesIndicator from '@provise/views/messages/indicator.vue'
import config from '@/config'

const { refresh: getLogoutLink, model: logoutLink } = useApi({ url: 'logout' })
const { user } = storeToRefs(useUserStore())
const lockStore = useLockStore()
const { lockKeys } = storeToRefs(lockStore)
const { fullscreen } = storeToRefs(useUiStore())
const router = useRouter()

useInactivityTracker(logout)

const { refresh: logoutApi } = useApi({
  url: 'logout',
  method: 'get',
})

const selected = ref(config.VITE_SERVICE_SELF)

const { model: redirect, refresh: redirectApi } = useApi({
  url: 'sso_redirect',
  query: computed(() => ({
    url: getHost(selected.value),
    service: selected.value,
  })),
  method: 'post',
})

const { model: link_options, refresh: requestLinkOptions } = useApi({
  url: 'user_services',
  immediate: true,
  pick: d => d.services || [],
})

const services = computed(() => {
  const lo = link_options.value || []

  return [
    {
      key: 'rtms',
      title: 'ARTAS',
      fullname: `<b>A</b>dvanced <b>R</b>eagent <b>T</b>racking <b>A</b>ccounting <b>S</b>ystem`,
    },
    {
      key: 'qems',
      title: 'TRACE',
      fullname: `<b>T</b>icket <b>R</b>esponse and <b>A</b>nalysis of <b>C</b>ommunication <b>E</b>ngagements`,
    },
    {
      key: 'qdms',
      title: 'LOQID',
      fullname: `<b>L</b>ibrary <b>O</b>f <b>Q</b>uality <b>I</b>nformation <b>D</b>ata`,
    },
  ].filter(({ key }) => lo.includes(key))
})

const selectedService = computed(() => {
  const sv = services.value
  const se = selected.value
  return sv.find(({ key }) => se == key)
})

watch(selected, async () => {
  await navigate()
})

onMounted(async () => {
  await requestLinkOptions()
})

function getHost(service) {
  switch (service) {
    case 'rtms':
      return config.VITE_RTMS_URL
    case 'qems':
      return config.VITE_QEMS_URL
    case 'qdms':
      return config.VITE_QDMS_URL
  }
  return 'about:blank'
}

async function navigate() {
  try {
    await redirectApi()
    window.location.href = redirect.value.url
  } catch (error) {
    throw new Error('Ошибка при запросе редиректа:', error)
  }
}

async function logout() {
  if (Object.keys(lockKeys.value).length > 0) {
    if (!confirm('Есть несохранённые изменения. Продолжить?')) {
      return
    }
    await lockStore.resetAll()
  }
  if (user.value.sso) {
    await getLogoutLink()
    window.location.assign(logoutLink.value.logout_uri)
  } else {
    logoutApi()
  }
  user.value = undefined
  router.push({ name: 'home' })
}
</script>
<template>
  <div
    v-if="!fullscreen"
    class="navbar navbar-expand-sm bg-bgsec topbar"
  >
    <div class="container-fluid">
      <div
        class="hstack gap-3"
        v-if="selectedService"
      >
        <div class="navbar-nav">
          <div
            class="nav-item dropdown"
            v-dropdown
          >
            <a
              href="#"
              class="dropdown-toggle"
              >{{ selectedService.title }}</a
            >

            <ul class="dropdown-menu">
              <li
                v-for="item of services"
                :key="item.key"
                class="dropdown-item"
              >
                <a
                  href="#"
                  @click.prevent="selected = item.key"
                  v-if="item.key != selected"
                >
                  {{ item.title }}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <span
          class="service-fullname"
          v-html="selectedService.fullname"
        />
      </div>
      <img src="@provise/img/logo.webp" id="logo"/>

      <div class="hstack gap-2">
        <slot name="indicators"></slot>

        <div
          class="dropdown"
          v-dropdown
        >
          <div class="dropdown-toggle">
            <x-icon icon="fas fa-gear" />
          </div>
          <ul class="dropdown-menu">
            <li class="dropdown-item"><a href="#">Сообщить об ошибке</a></li>
            <li class="dropdown-item"><a href="#">Руководство пользователя</a></li>
            <li class="dropdown-item"><a href="#">О системе</a></li>
          </ul>
        </div>

        <MessagesIndicator />

        <div
          class="dropdown"
          v-dropdown
        >
          <div class="dropdown-toggle">
            {{ user.fio || user.username }}
          </div>
          <ul class="dropdown-menu">
            <li class="dropdown-item">
              <RouterLink
                :to="{ name: 'profile.item' }"
                :active-class="''"
                title="Личный кабинет"
              >
                Личный кабинет
              </RouterLink>
            </li>
            <li class="dropdown-item"><a href="#">Настройки</a></li>
          </ul>
        </div>

        <button
          type="button"
          class="btn btn-sm btn-hover"
          @click="logout"
        >
          <x-icon icon="fa-solid fa-arrow-right-from-bracket" />
        </button>
      </div>
    </div>
  </div>
  <div class="navbar navbar-expand-sm bg-bgsec mainmenu">
    <div class="container-fluid">
      <Navbar />
    </div>
  </div>

  <div class="container-fluid">
    <main
      class="workspace"
      :class="{ borderless: $route.name == 'home' }"
    >
      <slot />
    </main>
  </div>
</template>
